import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Row, Col, Avatar, NackleCaption, Spin, Label } from '../';
import Select from 'react-select/async';
import { components } from 'react-select';
import { Search, ChevronDown, Close, ErrorFilled } from '@nackle/origami-icons';
import { getString } from '@nackle/intl-tools';
import { EmptyState } from './empty-state-illustration';
import classNames from 'classnames';
const env = window.location.hostname.toLowerCase().split( '.' ).find( function ( chunk ) {
    return [ 'ci', 'localhost', 'qa', 'pprd' ].indexOf( chunk ) >= 0;
} );
const baseProfileUrl = `${ window.location.origin }/api/v1/profile`;
const qaProfileUrl = 'https://qa.api.biw.cloud/v1/profile';

const LoadingMessage = ( props ) => {
    return (
        <div className="loading-menu">
            <div className="spin-container">
                <Spin size="small" { ...props.innerProps } spinning={ true }>
                </Spin>
            </div>
        </div>
    );
};

/**
 * Renders an empty state when no search results are found.
 * @ignore
 * @returns {JSX.Element} The NoOptions component.
 */
export const NoOptions = () => {
    return (
        <div className="empty-state-container">
            <EmptyState />
            <div>{ getString( 'origami.search.noResultsFound' ) }</div>
        </div>
    );
};

/**
 * Custom dropdown indicator component that displays a search icon.
 * @ignore
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child elements.
 * @returns {JSX.Element} The DropdownIndicator component.
 */
const DropdownIndicator = ( { children, ...props } ) => (
    <div className="icon-container">
        <Search className="icon" fill="#59606D" />
    </div>
);

/**
 * Custom multi-value label component for displaying selected items.
 * @ignore
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The MultiValueLabel component.
 */
const MultiValueLabel = ( props ) => {
    props.data.value = props.data.id;
    const { innerRef, innerProps } = props;
    return (
        <components.MultiValueLabel className="multi-label" key={ props.data.id } ref={ innerRef } { ...innerProps }>
            { `${ props.data.givenName } ${ props.data.surname }` }
        </components.MultiValueLabel>
    );
};

/**
 * Custom multi-value remove component for removing selected items.
 * @ignore
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The MultiValueRemove component.
 */
const MultiValueRemove = ( props ) => {
    return (
        <components.MultiValueRemove { ...props }>
            <Close />
        </components.MultiValueRemove>
    );
};

/**
 * Custom value container component with expand/collapse functionality.
 * @ignore
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child elements.
 * @returns {JSX.Element} The ValueContainer component.
 */
const ValueContainer = ( props ) => {
    const { children } = props;
    const [ expanded, setExpanded ] = useState( false );
    const [ showChevron, setShowChevron ] = useState( false );
    const [ scrollBar, setScrollBar ] = useState( false );
    const [ placeholder, setPlaceholder ] = useState();
    const valueRef = useRef();

    const containerClasses = classNames( {
        'value': true,
        'expanded': expanded ? true : false,
        'scroll': scrollBar ? true : false
    } );

    useEffect( () => {
        if ( valueRef.current?.clientHeight > 86 ) {
            setShowChevron( true );
        } else {
            setShowChevron( false );
        }
        if ( valueRef.current?.clientHeight > 199 ) {
            setScrollBar( true );
        } else {
            setScrollBar( false );
        }
    }, [ children ] );

    /**
     * Hides the placeholder text.
     */
    const hidePlaceholder = () => {
        const placeholder = document.querySelector( '.search-select__placeholder' );
        if ( placeholder ) {
            placeholder.style.display = 'none';
            setPlaceholder( placeholder );
        } else {
            setTimeout( () => {
                hidePlaceholder();
            }, 100 );
        }
    };

    /**
     * Shows the placeholder text if there are fewer than 3 children.
     */
    const showPlaceholder = () => {
        if ( children.length > 2 ) {
            return;
        } else {
            if ( placeholder ) {
                placeholder.role = 'presentation';
                placeholder.style.display = 'block';
            }
        }
    };

    /**
     * Toggles the expanded state and adjusts the chevron position.
     */
    const expand = () => {
        const chevron = document.querySelector( '.chevron' );
        chevron.style.top = '';
        setExpanded( !expanded );
    };
    return (
        <components.ValueContainer className={ containerClasses } { ...props } >
            <div className="ref-container" ref={ valueRef } onFocus={ hidePlaceholder } onBlur={ showPlaceholder }>
                { children }
                {
                    showChevron &&
                    <div className="search-chevron-container">
                        <div role="button" tabIndex={ 0 } onKeyUp={ ( event ) => { if ( event.key === 'Enter' ) { expand(); } } } onClick={ () => { expand(); } } onTouchEnd={ () => expand() } className='chevron'>
                            <ChevronDown className={ expanded ? 'up' : 'down' } fill={ '#59606D' } />
                        </div>
                    </div>
                }

            </div>
        </components.ValueContainer>
    );
};
export const Control = ( props ) => {
    const { children, selectProps } = props;
    const { hasError } = selectProps;
    return (
        <components.Control { ...props }>
            {
                hasError &&
                <div className="search-error-container">
                    <ErrorFilled className={ 'error-icon' } />

                </div>
            }
            { children }
        </components.Control>
    );

};
/**
 * Custom option component for displaying search results.
 * @ignore
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The CustomOption component.
 */
export const CustomOption = ( props ) => {
    const { innerProps, innerRef } = props;
    const person = props.data;
    const personDetails = [];
    if ( person.jobTitle ) {
        personDetails.push( person.jobTitle );
    }
    if ( person.department ) {
        personDetails.push( person.department );
    }
    if ( person.country ) {
        personDetails.push( person.country );
    }
    const personString = personDetails.join( ', ' );
    const label = (
        <div className={ person.disabled ? 'cursor-not-allowed' : '' }>
            <div className={ person.disabled ? 'disabled-search-result-wrapper disabled-search-result' : 'search-result-wrapper' } role="menuitem" tabIndex={ 0 } ref={ innerRef } key={ person.id } onKeyUp={ ( event ) => { if ( event.key === 'Enter' ) { props.selectOption( event ); } } } onClick={ ( event ) => { props.selectOption( event ); } } { ...innerProps } >
                <div className={ person.disabled ? 'disabled search-result' : 'search-result' }>
                    <span className="avatar-container">
                        <Avatar size={ 'medium' } alt={ getString( 'origami.avatar.alt' ) } className="primary-background-avatar" src={ `${ env === 'localhost' ? qaProfileUrl : baseProfileUrl }/public/images/image/${ person.companyId }/${ person.id }/avatar/170/170` }>{ person.givenName.slice( 0, 1 ) }</Avatar>
                    </span>
                    <div className="disabled-text-container">
                        <div className="right-search-result">

                            <span className="text-container">
                                <div className="search-name">
                                    { `${ person.givenName } ${ person.surname }` }
                                </div>
                                <div className="person-details" key={ person.id }>
                                    { personString }
                                </div>
                            </span>
                        </div>
                        <div className="disabled-reason">
                            { person.disabledReason }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return label;
};
/**
 * Custom MenuList component that displays search limit messages.
 * @ignore
 * @param {Object} props - The component props passed from react-select
 * @param {number} [props.limit] - Optional limit number to display in the message
 * @returns {JSX.Element} MenuList component with search limit messages
 */
const MenuList = ( props, limit ) => {
    return (
        <components.MenuList { ...props }>
            {
                props.options && props.options.length > 0 &&

                <div className="search-limit"><span className="top-100">{ getString( 'origami.search.100', { limit: limit ? limit : 100 } ) }</span> <span className="search-more">{ getString( 'origami.search.type' ) }</span></div>
            }
            { props.children }
            {
                props.options && props.options.length > 0 &&
                <div className="search-limit"><span className="top-100">{ getString( 'origami.search.100', { limit: limit ? limit : 100 } ) }</span> <span className="search-more">{ getString( 'origami.search.type' ) }</span></div>
            }
        </components.MenuList>
    );
};
/**
 * Default MenuList component that renders the standard react-select menu list
 * @ignore
 * @param {Object} props - The component props passed from react-select
 * @param {React.ReactNode} props.children - The child elements to render in the menu list
 * @returns {JSX.Element} Standard MenuList component with children
 */
const DefaultMenuList = ( props ) => {
    return (
        <components.MenuList { ...props }>
            { props.children }
        </components.MenuList>
    );
};

/**
 * Returns a function which can be used to delay the execution of the callback it is given.
 * @ignore
 */
function useDebounce() {
    const timer = useRef( null );

    useEffect( () => {
        // remove any lingering timers after unmount
        return () => clearTimeout( timer.current );
    }, [] );

    return ( delay, callback, ...args ) => {
        // make sure any existing timer doesn't execute
        clearTimeout( timer.current );
        timer.current = setTimeout( () => callback( ...args ), delay );
    };
}

/**
 * NacklePersonSearch component for searching and selecting people.
 *
 * @module NacklePersonSearch
 * @param {Object} props - The component props
 * @param {string} [props.placeholder] - Placeholder text for the search input - deprecated, labelText preferred
 * @param {string} [props.teamLabel] - Label text for the team section
 * @param {boolean} [props.showTeam] - Whether to show the team section
 * @param {string} [props.emptyTeamLabel] - Label to show when team section is empty
 * @param {string} [props.ariaLabel] - ARIA label for accessibility
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} [props.selectClassName] - Additional CSS class name for the select element
 * @param {boolean} [props.hasError] - Whether the component is in an error state
 * @param {string} [props.errorLabel] - Error message to display
 * @param {Array} [props.selectedUsers=[]] - Array of pre-selected users
 * @param {boolean} [props.searchLimitExceeded] - Whether search results limit is exceeded - deprecated
 * @param {boolean} [props.teamLimitExceeded] - Whether team members limit is exceeded
 * @param {number} [props.searchLimit] - Maximum number of search results to show
 * @param {number} [props.teamLimit] - Maximum number of team members to show
 * @param {string|number} [props.teamMaxHeight] - Maximum height of team section
 * @param {Array} [props.teamMembers] - Array of team members
 * @param {Function} [props.onClick] - Click handler function
 * @param {boolean} [props.noMulti] - Whether to allow multiple selections
 * @param {Function} props.searchPersons - Function to search for persons
 * @param {string} props.companyId - Company ID
 * @param {Object} props.user - User object containing claims
 * @param {string} [props.disabledLabel] - Label for disabled options
 * @param {boolean} [props.selfSelect] - Whether to allow the current user to select themselves
 * @param {Function} [props.onSelect] - Selection change handler
 * @param {Function} [props.labelText] - Text for the main select input's label
 * @param {Function} [props.size] - input size. Default is large, one of [small, medium, large]
 * @param {React.Ref} ref - Ref object for the component
 * @returns {JSX.Element} The NacklePersonSearch component
 */

const NacklePersonSearch = forwardRef( function NackleSearch( props, ref ) {
    const {
        placeholder,
        teamLabel,
        showTeam,
        emptyTeamLabel,
        ariaLabel,
        className,
        selectClassName,
        hasError,
        errorLabel,
        selectedUsers = [],
        searchLimitExceeded,
        teamLimitExceeded,
        searchLimit,
        teamLimit,
        teamMaxHeight,
        teamMembers,
        onClick,
        noMulti,
        labelText,
        size = 'large'
    } = props;
    const mappedUsers = selectedUsers.map( user => {
        user.label = `${ user.givenName } ${ user.surname }`;
        user.value = user.id;
        return user;
    } );
    const debounce = useDebounce();
    const [ selected, setSelected ] = useState( mappedUsers );
    const [ options, ] = useState( [] );
    const [ displaySearchLimit, setDisplaySearchLimit ] = useState( searchLimitExceeded );
    const [ displayTeamLimit, setDisplayTeamLimit ] = useState( teamLimitExceeded );
    const [ displayPlaceholder, setDisplayPlaceholder ] = useState( true );
    const [ inputValue, setInputValue ] = useState( '' );
    //get preselected/selected people for tag population
    const selectedIds = selected.map( person => {
        return person.id;
    } );

    //filter out already selected team members so they can't be added twice
    const filtered = [];
    if ( teamMembers ) {
        teamMembers.map( member => {
            if ( !selectedIds.includes( member.id ) ) {
                filtered.push( member );
            }
        } );
    }
    const theme = ( theme ) => ( {
        ...theme,
        spacing: {
            ...theme.spacing,
            controlHeight: 44,
            baseUnit: 1,
        }
    } );
    const selectPerson = ( person ) => {
        if ( !noMulti ) {
            setSelected( [ ...person ] );
            if ( typeof props.onSelect === 'function' ) {
                props.onSelect( person );
            }
        } else {
            if ( typeof props.onSelect === 'function' ) {
                props.onSelect( ...person );
            }
        }
    };


    const handleSearch = ( value, callback ) => {
        const { searchPersons, companyId, user, disabledLabel, selfSelect } = props;
        searchPersons( value ).then( resp => {
            let persons = [];
            if ( Array.isArray( resp ) ) {
                persons = resp;
            } else {
                const { results, totalResults } = resp;
                if ( totalResults ) {
                    if ( totalResults > searchLimit ) {
                        setDisplaySearchLimit( true );
                    } else {
                        setDisplaySearchLimit( false );
                    }

                }
                persons = results;
            }
            const companyPersons = persons.map( person => {
                person.companyId = companyId;
                person.value = person.id;
                return person;
            } );
            if ( !selfSelect ) {
                companyPersons.forEach( person => {
                    if ( person.id === user.claims.person_id ) {
                        person.disabled = true;
                        person.disabledReason = disabledLabel;
                    }
                } );
            }
            callback( companyPersons );
        } );
    };

    const handleKeyPress = ( e, callback, variables ) => {
        if ( e.key === 'Enter' ) {
            if ( typeof callback === 'function' ) {
                if ( variables ) {
                    callback( variables );
                } else {
                    callback();
                }
            }
        }
    };
    const handleInputChange = ( newValue ) => {
        if ( newValue.length === 0 && inputValue.length === 1 ) {
            setDisplayPlaceholder( false );
        }
        setInputValue( newValue );
    };
    const handleSelect = ( options, action ) => {
        if ( noMulti ) {

            setDisplayPlaceholder( false );
            if ( typeof props.onSelect === 'function' ) {
                props.onSelect( action.option );
            }
            return;
        }
        switch ( action.action ) {
            case 'clear':
                selectPerson( [] );
                break;
            case 'remove-value':
                const filtered = selected.filter( person => person.id !== action.removedValue.id );
                selectPerson( filtered );
                break;
            case 'select-option':
                if ( selected.length === 0 ) {
                    selectPerson( options );
                } else if ( selected.length > 0 ) {
                    selected.push( action.option );
                    selectPerson( selected );
                }
                break;

            default:
                console.log( 'unknown action' );
                break;
        }
    };
    const searchClasses = classNames( {
        'content-container': true,
        'search-screen': true,
        [ className ]: className ? true : false,
        [ size ]: true
    } );
    useEffect( () => {
        setDisplaySearchLimit( searchLimitExceeded );
        setDisplayTeamLimit( teamLimitExceeded );
    }, [ searchLimitExceeded, teamLimitExceeded ] );

    return (
        <div className={ searchClasses } ref={ ref }>
            <Row gutter={ [ 24, 24 ] }>
                <Col span={ 24 }>
                    <Label text={ labelText } size={ size }>
                        <Select
                            aria-label={ ariaLabel }
                            hasError={ hasError }
                            components={ {
                                DropdownIndicator,
                                Option: CustomOption,
                                MultiValueLabel,
                                LoadingIndicator: null,
                                LoadingMessage,
                                ValueContainer,
                                Control,
                                MenuList: displaySearchLimit ? ( props ) => MenuList( props, searchLimit ) : DefaultMenuList,
                                MultiValueRemove
                            } }
                            noOptionsMessage={ hasError ? () => { return null; } : NoOptions }
                            classNamePrefix={ 'search-select' }
                            styles={ {
                                control: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    flexDirection: 'row-reverse',
                                    borderRadius: '8px',
                                    border: hasError ? '1px solid #de1b1b' : '1px solid #C9D1DF',
                                    '&:hover': {
                                        border: hasError ? '1px solid #de1b1b' : '1px solid #C9D1DF',
                                    },
                                    '&:focus': {
                                        boxShadow: '0 0 4px 0 rgba(50, 114, 217, 0.75)'
                                    }


                                } ),
                                indicatorSeparator: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    display: 'none'
                                } ),
                                indicatorsContainer: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    marginLeft: '10px',
                                    marginRight: '10px'
                                } ),
                                valueContainer: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    paddingTop: '12px',
                                    paddingBottom: '12px',
                                    gap: '4px'
                                } ),
                                multiValue: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    height: '24px',
                                    paddingLeft: '10px',
                                    backgroundColor: '#F5F7FA',
                                    borderRadius: '6px',
                                    lineHeight: '24px',
                                    fontSize: '16px',
                                    overflow: 'hidden'
                                } ),
                                multiValueRemove: ( baseStyles, state ) => ( {
                                    ...baseStyles,
                                    width: '34px',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: '#F5F7FA',
                                        cursor: 'pointer',
                                        color: 'inherit'
                                    },
                                    'svg': {
                                        width: '16px',
                                        height: '16px',
                                        color: '#59606d'
                                    }
                                } )
                            } }
                            theme={ theme }
                            openMenuOnFocus={ false }
                            openMenuOnClick={ false }
                            onFocus={ onClick }
                            isClearable={ false }
                            isMulti={ true }
                            autoFocus={ false }
                            inputValue={ inputValue }
                            onInputChange={ handleInputChange }
                            menuPlacement={ 'auto' }
                            onBlur={ () => setDisplayPlaceholder( true ) }
                            className={ selectClassName ? `${ selectClassName } person-search` : 'person-search' }
                            options={ options }
                            value={ selected }
                            onChange={ ( value, action ) => handleSelect( value, action ) }
                            placeholder={ labelText ? false : displayPlaceholder ? placeholder || '' : false }
                            loadOptions={ ( ...args ) => debounce( 300, handleSearch, ...args ) } />
                    </Label>
                </Col>
            </Row>
            {
                hasError && errorLabel &&
                <NackleCaption className="error-label sub-description" size={ size } text={ errorLabel } />
            }
            { showTeam &&

                ( filtered && filtered.length > 0 ?
                    <>
                        <div className="team-container" >
                            <Label size={ size } className="sub-description" text={ teamLabel || 'Add people from your team.' } />
                            <Row className="scroll-container" style={ { maxHeight: teamMaxHeight ? teamMaxHeight : 'none' } }>
                                <Col span={ 24 }>
                                    {
                                        displayTeamLimit &&
                                        <div className="search-limit top"><span className="top-100">{ getString( 'origami.search.100', { limit: teamLimit ? teamLimit : 100 } ) }</span> <span className="search-more">{ getString( 'origami.search.more' ) }</span></div>
                                    }


                                    { filtered.map( person => {
                                        const personDetails = [];
                                        if ( person.jobTitle ) {
                                            personDetails.push( person.jobTitle );
                                        }
                                        if ( person.department ) {
                                            personDetails.push( person.department );
                                        }
                                        personDetails.push( person.country );
                                        const personString = personDetails.join( ', ' );
                                        return (
                                            <div className={ person.disabled ? 'disabled search-result' : 'search-result' } tabIndex="0" role="menuitem" key={ person.id } onKeyUp={ person.disabled ? () => { } : () => handleKeyPress( event, selectPerson, [ ...selected, person ] ) } onClick={ person.disabled ? () => { } : () => { selectPerson( [ ...selected, person ] ); } }>
                                                <span className="avatar-container">
                                                    <Avatar alt={ getString( 'origami.avatar.alt' ) } size={ 'medium' } className="primary-background-avatar" src={ `${ env === 'localhost' ? qaProfileUrl : baseProfileUrl }/public/images/image/${ props.companyId }/${ person.id }/avatar/170/170` }>{ person.givenName.slice( 0, 1 ) }</Avatar>
                                                </span>
                                                <div className="disabled-text-container">
                                                    <div className="right-search-result">

                                                        <span className="text-container">
                                                            <div className="search-name">
                                                                { `${ person.givenName } ${ person.surname }` }
                                                            </div>
                                                            <div className="person-details" key={ person.id }>
                                                                { personString }
                                                            </div>
                                                        </span>
                                                    </div>
                                                    <div className="disabled-reason">
                                                        { person.disabledReason }
                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    } )
                                    }

                                    {
                                        displayTeamLimit &&
                                        <div className="search-limit"><span className="top-100">{ getString( 'origami.search.100', { limit: teamLimit ? teamLimit : 100 } ) }</span> <span className="search-more">{ getString( 'origami.search.more' ) }</span></div>
                                    }
                                </Col>
                            </Row>
                        </div>
                    </>
                    :
                    <div className="no-team-container">
                        <EmptyState />
                        <div className="search-text-no-team">{ emptyTeamLabel || getString( 'origami.search.noResultsFound' ) }</div>
                    </div>
                ) }

        </div> );
} );

export { NacklePersonSearch };